import AppLayout from '../layouts/AppLayout'
import React, { useState, useEffect, useContext, useMemo } from 'react'
import { AppContext } from '../contexts/AppContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { HiArrowNarrowRight } from 'react-icons/hi'
import { FiPlus, FiDownload } from 'react-icons/fi'
import { MdOutlineRemoveCircle, MdOutlineDelete } from 'react-icons/md'
import '../styles/match.css'
import Swal from 'sweetalert2'
import WorkHeader from '../components/WorkHeader'
import { CONTENT_TYPES, contentTypeLabel, SEARCH_PLATFORMS } from '../constants/domain'
import DataTable from '../components/DataTable'

const formatTimestamp = (timestamp) => {
  if (!timestamp) return ''
  let str = typeof timestamp === 'string' ? timestamp : String(timestamp)
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) str += 'Z'
  const date = new Date(str)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

const formatElapsedTime = (addedat, deletedat) => {
  if (!addedat || !deletedat) return ''

  // Ensure Zulu (Z) suffix if not present
  let addedStr = typeof addedat === 'string' ? addedat : String(addedat)
  let deletedStr = typeof deletedat === 'string' ? deletedat : String(deletedat)
  if (!addedStr.endsWith('Z') && !addedStr.includes('+') && !addedStr.includes('-', 10)) {
    addedStr += 'Z'
  }
  if (!deletedStr.endsWith('Z') && !deletedStr.includes('+') && !deletedStr.includes('-', 10)) {
    deletedStr += 'Z'
  }

  const addedMs = new Date(addedStr).getTime()
  const deletedMs = new Date(deletedStr).getTime()
  if (isNaN(addedMs) || isNaN(deletedMs)) return ''
  const diffMs = deletedMs - addedMs
  if (diffMs < 0) return ''
  const totalMinutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} Minutes`
  if (minutes === 0) return `${hours} Hours`
  return `${hours} Hours ${minutes} Minutes`
}


const MatchDetails = () => {
  const { matchId } = useParams()

  const navigate = useNavigate()

  const [match, setMatch] = useState('')
  const [categories, setCategories] = useState([])
  const [type, setType] = useState('Live')
  const [linksInput, setlinksInput] = useState('')
  const [linksLoading, setslinksLoading] = useState(false)
  const [showLinkForm, setshowLinkForm] = useState(false)
  const [linkFormLoading, setlinkFormLoading] = useState(false)
  const [linksError, setlinksError] = useState('')
  const [matchLinks, setmatchLinks] = useState([])
  const [filteredLinks, setFilteredLinks] = useState([])
  const [liveCount, setliveCount] = useState(0)
  const [highlightCount, sethighlightCount] = useState(0)
  const [googleCount, setgoogleCount] = useState(0)
  const [totalSpectators, settotalSpectators] = useState(0)
  const [websitesLinks, setwebsitesLinks] = useState([])
  const [websites, setWebsite] = useState([])
  const [specwebsites, setspecWebsite] = useState([])
  const [spectatorCounts, setSpectatorCounts] = useState({})
  const [showSpecButton, setshowSpecButton] = useState(false)
  const [specFormLoading, setspecFormLoading] = useState(false)
  const [activeSection, setActiveSection] = useState('links')
  const [selectedLinkIds, setSelectedLinkIds] = useState([])
  const [excelDownloading, setExcelDownloading] = useState(false)

  const {
    getmatch,
    getlinkcategories,
    getmatchlinks,
    downloadmatchlinksexcel,
    getmatchwebsites,
    getspecwebsites,
    updatematchspectators,
    addmatchlinks,
    deletematchlink,
    setmatchlinkstakendown,
    setmatchlinkstakendownbulk,
    deletematchlinks,
    removematchlinks,
    isLoading,
  } = useContext(AppContext)

  const fetchMatch = async () => {
    const response = await getmatch(matchId)
    if (response && response[0]) {
      setMatch(response[0])
    } else {
      navigate('/all-matches')
    }
  }

  useEffect(() => {
    if (matchId) {
      fetchMatch()
      fetchMatchWebsites()
    }
  }, [])

  useEffect(() => {
    if (matchId) {
      fetchMatchlinks(type)
    }
  }, [type])

  const handleSetType = async (t) => {
    setType(t)
    setlinksInput('')
    setshowLinkForm(false)
    setlinksError(false)
    setSelectedLinkIds([])
    fetchMatchlinks(t)
  }

  const fetchCategories = async () => {
    const response = await getlinkcategories(type, matchId)
    setliveCount(response[0].totallivelinks)
    sethighlightCount(response[0].totalhighlightlinks)
    setCategories(response)
  }

  const fetchMatchlinks = async (type) => {
    setslinksLoading(true)
    const response = await getmatchlinks(type, matchId)
    setmatchLinks(response.matchlinks)
    setliveCount(response.totallive)
    sethighlightCount(response.totalhighlights)
    setgoogleCount(response.totalgoogle)
    setwebsitesLinks(response.totalwebsites)
    settotalSpectators(response.totalspectators)
    setslinksLoading(false)
  }

  const fetchMatchWebsites = async () => {
    const response = await getmatchwebsites(matchId)
    setWebsite(response)
    const response2 = await getspecwebsites(matchId)
    setspecWebsite(response2)
  }

  const handleDownloadExcel = async () => {
    if (excelDownloading) return
    try {
      setExcelDownloading(true)
      const filenameHint = match && match.title ? match.title : matchId
      await downloadmatchlinksexcel(matchId, filenameHint)
    } finally {
      setExcelDownloading(false)
    }
  }

  const handleLinksSubmit = async (e) => {
    e.preventDefault()
    if (!isLoading) {
      try {
        setlinkFormLoading(true)
        if (linksInput !== '') {
          // Split input links into array and filter empty lines
          let inputLinks = linksInput.split('\n').filter((link) => link.trim() !== '')

          inputLinks = [...new Set(inputLinks)]

          const existingLinks = matchLinks.map((ml) => ml.link.trim())

          // Filter out links that already exist
          const newLinks = inputLinks.filter((link) => !existingLinks.includes(link.trim()))

          if (newLinks.length === 0) {
            setlinksError('All links already exist')
            return
          }

          // Join filtered links back into string with newlines
          const filteredLinksInput = newLinks.join('\n')
          setlinksError('')
          const addedat = new Date().toISOString().slice(0, 19)
          let response = await addmatchlinks(filteredLinksInput, type, matchId, addedat)
          if (response === 'success') {
            setlinksInput('')
            setshowLinkForm(false)
            fetchMatchlinks(type)
          }
        } else {
          setlinksError('All fields are required')
          return
        }
      } catch (error) {
        setlinksError('Error: ' + error)
      } finally {
        setlinkFormLoading(false)
      }
    }
  }

  const getDomain = (link, domain) => {
    if (domain) return domain
    try {
      return new URL(link).hostname
    } catch {
      return ''
    }
  }

  const selectableLinkIds = useMemo(() => filteredLinks.map((l) => l.id), [filteredLinks])
  const allSelectableSelected =
    selectableLinkIds.length > 0 && selectableLinkIds.every((id) => selectedLinkIds.includes(id))

  const websiteIconMap = useMemo(() => {
    const map = new Map()
    if (websitesLinks) {
      websitesLinks.forEach((website) => {
        if (website.link) {
          website.link.split(',').forEach((domain) => {
            map.set(domain.trim().toLowerCase(), website.icon)
          })
        }
      })
    }
    return map
  }, [websitesLinks])

  const getWebsiteIcon = (link) => {
    try {
      const hostname = new URL(link).hostname.toLowerCase().replace(/^www\./, '')
      for (const [domain, icon] of websiteIconMap) {
        if (hostname === domain || hostname.endsWith('.' + domain)) {
          return icon
        }
      }
    } catch {}
    return null
  }

  const linkTableColumns = useMemo(() => {
    const cols = []
    if (type === 'Live' || type === 'Highlight') {
      cols.push({
        Header: () => (
          <input
            type='checkbox'
            checked={allSelectableSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedLinkIds([...selectableLinkIds])
              } else {
                setSelectedLinkIds([])
              }
            }}
            title={allSelectableSelected ? 'Deselect all' : 'Select all'}
          />
        ),
        id: 'select',
        width: 42,
        Cell: ({ row }) => {
          const id = row.original.id
          const checked = selectedLinkIds.includes(id)
          return (
            <input
              type='checkbox'
              checked={checked}
              onChange={() => {
                setSelectedLinkIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
              }}
            />
          )
        },
      })
    }
    cols.push({
      Header: 'LINK',
      accessor: 'link',
      Cell: ({ value }) => {
        const icon = getWebsiteIcon(value)
        return (
          <div className='flex items-center gap-2'>
            {icon && (
              <img
                width='16'
                height='16'
                src={`${process.env.REACT_APP_STATIC_URL || ''}${icon}`}
              />
            )}
            <a
              href={value}
              target='_blank'
              rel='noreferrer'
              className='text-blue-600 hover:underline truncate block max-w-[400px]'
            >
              {value}
            </a>
          </div>
        )
      },
    })
    cols.push(
      { Header: 'TYPE', accessor: 'type', Cell: ({ value }) => contentTypeLabel(value) },
      {
        Header: 'DOMAIN',
        accessor: (row) => getDomain(row.link, row.domain),
        id: 'domain',
      },
      {
        Header: 'NOTICED TIME',
        accessor: 'addedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TAKE DOWN TIME',
        accessor: 'deletedat',
        Cell: ({ value }) => (value ? formatTimestamp(value) : '–'),
      },
      {
        Header: 'TOTAL TIME ELAPSED',
        id: 'elapsedtime',
        Cell: ({ row }) => {
          const { addedat, deletedat } = row.original
          const result = formatElapsedTime(addedat, deletedat)
          return result || '–'
        },
      }
    )
    if (type === 'Live' || type === 'Highlight') {
      cols.push({
        Header: '',
        id: 'actions',
        Cell: ({ row }) => (
          <div className='flex items-center gap-1'>
            <button
              type='button'
              className='p-2 rounded hover:bg-neutral-100 text-neutral-500 hover:text-red-500'
              onClick={() => handleDelete(row.original.id)}
              title='Enter take down time'
            >
              <MdOutlineRemoveCircle size={20} />
            </button>
            <button
              type='button'
              className='p-2 rounded hover:bg-neutral-100 text-neutral-500 hover:text-red-600'
              onClick={() => handleDeleteRow(row.original.id)}
              title='Remove link'
            >
              <MdOutlineDelete size={20} />
            </button>
          </div>
        ),
      })
    }
    return cols
  }, [type, selectedLinkIds, selectableLinkIds, allSelectableSelected, websiteIconMap])

  const handleDelete = async (id) => {
    if (!isLoading) {
      try {
        const result = await Swal.fire({
          title: 'Enter take down time',
          icon: 'info',
          html: `
            <label style="display:block;text-align:left;font-size:14px;font-weight:600;margin-bottom:4px;">Taken down at</label>
            <input type="datetime-local" step="1" id="swal-deletedat" class="swal2-input" style="width:100%;margin:0;" value="${new Date(
              Date.now() - new Date().getTimezoneOffset() * 60000
            )
              .toISOString()
              .slice(0, 19)}" />
          `,
          showCancelButton: true,
          confirmButtonColor: '#E51A4D',
          cancelButtonColor: '#2F2F2F',
          confirmButtonText: 'Confirm take down',
          preConfirm: () => {
            const val = document.getElementById('swal-deletedat').value
            return val ? new Date(val).toISOString().slice(0, 19) : null
          },
        })

        if (result.isConfirmed) {
          const deletedAt = result.value || null
          let response = await deletematchlink(id, deletedAt)
          if (response === 'success') {
            fetchMatchlinks(type)
          }
        }
      } catch (error) {
        Swal.fire('An Error Occurred', error, 'error')
      }
    }
  }

  const handleDeleteRow = async (id) => {
    if (isLoading) return
    const result = await Swal.fire({
      title: 'Remove this link?',
      text: 'This will permanently remove the link.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E51A4D',
      cancelButtonColor: '#2F2F2F',
      confirmButtonText: 'Remove',
    })
    if (result.isConfirmed) {
      const response = await removematchlinks([id])
      if (response === 'success') {
        fetchMatchlinks(type)
      }
    }
  }

  const handleBulkTakendown = async () => {
    if (isLoading || selectedLinkIds.length === 0) return
    try {
      const result = await Swal.fire({
        title: 'Set take down time for selected links',
        icon: 'info',
        html: `
          <label style="display:block;text-align:left;font-size:14px;font-weight:600;margin-bottom:4px;">Minutes elapsed since link was notified</label>
          <input type="number" min="0" id="swal-elapsed-minutes" class="swal2-input" style="width:100%;margin:0;" value="" placeholder="e.g. 10" />
        `,
        showCancelButton: true,
        confirmButtonColor: '#E51A4D',
        cancelButtonColor: '#2F2F2F',
        confirmButtonText: 'Set take down time',
        preConfirm: () => {
          const minutes = parseInt(document.getElementById('swal-elapsed-minutes').value, 10)
          if (isNaN(minutes) || minutes < 0) {
            Swal.showValidationMessage('Please enter a valid number of minutes (0 or more)')
            return false
          }
          return minutes
        },
      })
      if (result.isConfirmed) {
        const minutes = result.value
        const items = matchLinks
          .filter((l) => selectedLinkIds.includes(l.id))
          .map((link) => {
            const noticedTime = link.addedat ? new Date(link.addedat) : new Date()
            const deletedAt = new Date(noticedTime.getTime() + minutes * 60000).toISOString().slice(0, 19)
            return { id: link.id, deletedat: deletedAt }
          })
        const response = await setmatchlinkstakendownbulk(items)
        if (response === 'success') {
          setSelectedLinkIds([])
          fetchMatchlinks(type)
        }
      }
    } catch (error) {
      Swal.fire('An Error Occurred', error, 'error')
    }
  }

  const handleBulkDelete = async () => {
    if (isLoading || selectedLinkIds.length === 0) return
    const result = await Swal.fire({
      title: 'Take down selected links now?',
      text: `This will set the taken down time to now for ${selectedLinkIds.length} link(s), but keep them in history.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E51A4D',
      cancelButtonColor: '#2F2F2F',
      confirmButtonText: 'Take down now',
    })
    if (result.isConfirmed) {
      const response = await deletematchlinks(selectedLinkIds)
      if (response === 'success') {
        setSelectedLinkIds([])
        fetchMatchlinks(type)
      }
    }
  }

  useEffect(() => {
    const newCounts = {}
    websites.forEach((site) => {
      newCounts[site.id] = site.totalspectators || 0
    })
    setSpectatorCounts(newCounts)
  }, [websites])

  const handleSpectatorChange = (id, value) => {
    setSpectatorCounts((prev) => ({ ...prev, [id]: value }))
    setshowSpecButton(true)
  }

  const handleSubmitSpectators = async () => {
    if (!isLoading) {
      try {
        setspecFormLoading(true)
        let response = await updatematchspectators(matchId, spectatorCounts)
        if (response === 'success') {
          setshowSpecButton(false)
        }
      } catch (error) {
      } finally {
        setspecFormLoading(false)
      }
    }
  }

  return (
    <AppLayout pageName='Work Details'>
      {match !== '' ? (
        <>
          <WorkHeader work={match}>
            <Link
              to={`/edit-match/${match.matchid}`}
              className='bg-green-100 hover:bg-green-200 transition-colors text-main-alt text-xs font-medium px-2.5 py-2 rounded normal-font'
            >
              Edit Work
            </Link>
          </WorkHeader>
          <section className='relative'>
            <button
              type='button'
              onClick={handleDownloadExcel}
              disabled={excelDownloading}
              className='btn-main-inverse absolute top-0 right-0 disabled:opacity-60 m-2'
            >
              <span>Download Excel File</span>
              {excelDownloading ? (
                <Spinner
                  size={14}
                  animating={excelDownloading}
                />
              ) : (
                <FiDownload />
              )}
            </button>
            <div className='flex flex-col lg:flex-col justify-between items-center space-x-3'>
              <div className='text-center'>
                <h2 className='text-2xl font-bold tracking-normal text-main mb-2'>Infringing Links</h2>
                <div className='flex flex-row items-center space-x-3 mb-2'>
                  <div>
                    <span className='bg-blue-100 text-blue-800 text-sm font-medium px-3 py-2 rounded'>
                      Total Links:{' '}
                      {liveCount + highlightCount + googleCount > 0
                        ? (liveCount + highlightCount + googleCount).toLocaleString()
                        : '0'}
                    </span>
                  </div>
                  <div>
                    <span className='bg-green-100 text-green-800 text-sm font-medium px-3 py-2 rounded'>
                      Listens / Views:
                      {totalSpectators > 0
                        ? ` ${Intl.NumberFormat('en-US').format(parseInt(totalSpectators)).replace(/,/g, ' ')}`
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
              {websitesLinks && (
                <div className='flex flex-wrap flex-row items-center justify-center mb-4'>
                  {websitesLinks.map((website, index) => {
                    return (
                      <React.Fragment key={index}>
                        <div className='flex space-x-2 border rounded p-1 pr-2 m-2 items-center'>
                          <div>
                            <img
                              width='40'
                              height='40'
                              src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                            />
                          </div>
                          <div>
                            <h6 className='text-neutral-500 text-xs'>{website.name}</h6>
                            <h5 className='text-main-alt leading-none mt-0.5 font-bold'>
                              {SEARCH_PLATFORMS.includes(website.name) ? googleCount : website.total}
                            </h5>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
            <div className='pills w-full rounded-lg p-0 text-black font-semibold flex'>
              {CONTENT_TYPES.map((contentType) => {
                const count = { Live: liveCount, Highlight: highlightCount, Google: googleCount }[contentType.value]
                return (
                  <button
                    key={contentType.value}
                    className={`${
                      activeSection === 'links' && type === contentType.value
                        ? 'bg-white border border-b-white'
                        : 'hover:bg-neutral-50 border-b text-neutral-500'
                    } rounded-t-lg w-1/3 p-4 flex justify-center space-x-2`}
                    onClick={() => {
                      setActiveSection('links')
                      handleSetType(contentType.value)
                    }}
                  >
                    <span>{contentType.label}</span>
                    {count > 0 && (
                      <span className='bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded'>{count}</span>
                    )}
                  </button>
                )
              })}
            </div>
            {activeSection === 'links' && (
              <section className='space-y-6 !rounded-t-none !border-t-0'>
                <button
                  onClick={() => setshowLinkForm(!showLinkForm)}
                  className='btn-main-inverse mb-3'
                >
                  New Links <FiPlus />
                </button>
                {showLinkForm && (
                  <section>
                    <form
                      onSubmit={handleLinksSubmit}
                      method='post'
                    >
                      <div className='p-2'>
                        <textarea
                          name='linksinput'
                          rows={8}
                          className='form-control'
                          placeholder='Copy and paste the links here...'
                          required
                          onChange={(e) => setlinksInput(e.target.value)}
                        ></textarea>
                      </div>
                      <div className='p-2'>
                        <div
                          className='flex items-center p-4 border-l-4 border-neutral-400 bg-neutral-100'
                          role='alert'
                        >
                          <svg
                            className='flex-shrink-0 w-4 h-4'
                            aria-hidden='true'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path d='M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z' />
                          </svg>
                          <div className='ms-3 text-sm font-medium text-gray-700'>
                            Please make sure each link is in a separate line.
                          </div>
                        </div>
                      </div>
                      <div className='p-2 flex space-x-5 items-center'>
                        <button
                          type='submit'
                          className='btn-submit'
                          disabled={isLoading}
                        >
                          <span>Submit</span>
                          <span>
                            {linkFormLoading === false ? (
                              <HiArrowNarrowRight />
                            ) : (
                              <Spinner
                                size={14}
                                className='ml-2'
                                animating={linkFormLoading}
                              />
                            )}
                          </span>
                        </button>
                        {linksError !== '' ? (
                          <div className='text-red-500 font-semibold text-md tracking-wide'>{linksError}</div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </form>
                  </section>
                )}

                <div>
                  {(type === 'Live' || type === 'Highlight') && selectedLinkIds.length > 0 && (
                    <div className='flex items-center gap-3 mb-3'>
                      <span className='text-sm text-neutral-600'>{selectedLinkIds.length} selected</span>
                      <button
                        type='button'
                        onClick={handleBulkTakendown}
                        className='px-3 py-1.5 rounded bg-main text-white text-sm hover:opacity-90'
                      >
                        Set taken down time
                      </button>
                      <button
                        type='button'
                        onClick={() => setSelectedLinkIds([])}
                        className='px-3 py-1.5 rounded border border-neutral-300 text-sm hover:bg-neutral-50'
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                  {linksLoading ? (
                    <div className='flex justify-center w-full py-12'>
                      <Spinner
                        color='#CCCCCC'
                        size={24}
                      />
                    </div>
                  ) : (
                    <DataTable
                      data={matchLinks}
                      columns={linkTableColumns}
                      defaultPageSize={50}
                      pageSizeOptions={[10, 25, 50, 100]}
                      onFilteredRowsChange={setFilteredLinks}
                    />
                  )}
                </div>
              </section>
            )}
          </section>
          <section>
            <h2 className='text-2xl font-bold tracking-normal text-main'>Listens / Views on Pirated Copies</h2>
            <table className='table mt-3'>
              <thead>
                <tr>
                  <th
                    scope='col'
                    className='px-6 py-4 text-left'
                  >
                    Platform
                  </th>
                  <th
                    scope='col'
                    className='px-6 py-4'
                  >
                    Listens / Views
                  </th>
                </tr>
              </thead>
              <tbody>
                {specwebsites.map((website, index) => {
                  return (
                    <tr
                      key={website.id}
                      className='border-t'
                    >
                      <td
                        scope='row'
                        className='px-6 py-4 font-medium whitespace-nowrap text-left'
                      >
                        <div className='flex space-x-2 items-center'>
                          <img
                            width='16'
                            height='16'
                            src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                          />
                          <h6 className='text-neutral-900 text-lg'>{website.name}</h6>
                        </div>
                      </td>
                      <td className='px-6 py-3 text-lg text-center'>
                        <input
                          className='form-control py-1 text-center border'
                          value={spectatorCounts[website.id] || 0}
                          onChange={(e) => handleSpectatorChange(website.id, parseInt(e.target.value))}
                        />
                      </td>
                    </tr>
                  )
                })}
                <tr>
                  <td></td>
                  <td className='px-6 py-3 text-lg text-center'>
                    {showSpecButton && (
                      <button
                        onClick={handleSubmitSpectators}
                        className='btn-submit mx-auto'
                      >
                        Save changes
                      </button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </>
      ) : (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      )}
    </AppLayout>
  )
}

export default MatchDetails
